import React, { useState } from 'react';
import { KeyRound, LogOut, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCurator } from '../../context/CuratorContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

/**
 * Unobtrusive curator login / logout control for the sidebar footer.
 * Password is only held in component state while the form is open — never persisted.
 */
export default function CuratorControls({ collapsed = false }) {
  const { isCurator, login, logout, checking } = useCurator();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const handleLogin = async (e) => {
    e?.preventDefault?.();
    if (!password || busy) return;
    setBusy(true);
    try {
      await login(password);
      setPassword('');
      setOpen(false);
      toast.success('Curator session active', {
        description: 'Use Set as Landing on any analysis to pin the visitor home view.',
      });
    } catch (err) {
      const status = Number(err?.status || err?.response?.status || 0);
      toast.error(status === 401 ? 'Invalid credentials' : 'Login failed', {
        description: status === 401 ? 'Try again.' : (err?.message || 'Could not reach curator auth.'),
      });
    } finally {
      setBusy(false);
      setPassword('');
    }
  };

  const handleLogout = async () => {
    setBusy(true);
    try {
      await logout();
      toast.success('Curator logged out');
    } finally {
      setBusy(false);
      setOpen(false);
      setPassword('');
    }
  };

  if (checking && !isCurator) {
    return null;
  }

  if (isCurator) {
    return (
      <div className={`px-2 mt-2 ${collapsed ? 'flex justify-center' : ''}`} data-testid="curator-session">
        <button
          type="button"
          onClick={handleLogout}
          disabled={busy}
          data-testid="curator-logout-btn"
          title="End curator session"
          className={`flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#f5b400]/80 hover:text-[#f5b400] transition-colors ${
            collapsed ? 'p-2' : 'w-full px-2 py-1.5 rounded-sm hover:bg-[#f5b400]/10'
          }`}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <LogOut className="w-3 h-3" />}
          {!collapsed && <span>Curator logout</span>}
        </button>
      </div>
    );
  }

  return (
    <div className={`px-2 mt-2 ${collapsed ? 'flex flex-col items-center' : ''}`} data-testid="curator-login-wrap">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="curator-login-btn"
          title="Curator Login"
          className={`flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#a1a1aa]/40 hover:text-[#a1a1aa] transition-colors ${
            collapsed ? 'p-2' : 'w-full px-2 py-1.5 rounded-sm hover:bg-white/5'
          }`}
        >
          <KeyRound className="w-3 h-3" />
          {!collapsed && <span>Curator Login</span>}
        </button>
      ) : (
        <form
          onSubmit={handleLogin}
          className={`space-y-1.5 ${collapsed ? 'w-full max-w-[140px]' : 'w-full'}`}
          data-testid="curator-login-form"
        >
          <Input
            type="password"
            autoComplete="current-password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="curator-password-input"
            className="h-8 text-xs bg-[#14161a] border-white/10"
            disabled={busy}
          />
          <div className="flex gap-1">
            <Button
              type="submit"
              size="sm"
              disabled={busy || !password}
              data-testid="curator-login-submit"
              className="flex-1 h-7 text-[10px] bg-[#f5b400] text-black hover:bg-[#f5b400]/90"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Login'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => {
                setOpen(false);
                setPassword('');
              }}
              className="h-7 text-[10px] text-[#a1a1aa]"
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
